import { useCallback, useEffect, useRef, useState } from "react";

interface PullToRefreshOptions {
	onRefresh: () => void;
	enabled?: boolean;
	threshold?: number;
}

export function usePullToRefresh({
	onRefresh,
	enabled = true,
	threshold = 80,
}: PullToRefreshOptions) {
	const [pulling, setPulling] = useState(false);
	const [progress, setProgress] = useState(0);
	const startY = useRef(0);
	const containerRef = useRef<HTMLDivElement>(null);

	const onTouchStart = useCallback(
		(e: TouchEvent) => {
			if (!enabled) return;
			const el = containerRef.current;
			if (!el) return;
			// Only trigger when at the top of the scroll container
			if (el.scrollTop > 5) return;
			startY.current = e.touches[0].clientY;
		},
		[enabled]
	);

	const onTouchMove = useCallback(
		(e: TouchEvent) => {
			if (!enabled) return;
			const el = containerRef.current;
			if (!el || el.scrollTop > 5) return;
			const dy = e.touches[0].clientY - startY.current;
			if (dy > 0 && dy < threshold * 2) {
				setPulling(true);
				setProgress(Math.min(dy / threshold, 1));
			}
		},
		[enabled, threshold]
	);

	const onTouchEnd = useCallback(() => {
		if (progress >= 1) {
			onRefresh();
		}
		setPulling(false);
		setProgress(0);
	}, [progress, onRefresh]);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		el.addEventListener("touchstart", onTouchStart, { passive: true });
		el.addEventListener("touchmove", onTouchMove, { passive: true });
		el.addEventListener("touchend", onTouchEnd, { passive: true });
		return () => {
			el.removeEventListener("touchstart", onTouchStart);
			el.removeEventListener("touchmove", onTouchMove);
			el.removeEventListener("touchend", onTouchEnd);
		};
	}, [onTouchStart, onTouchMove, onTouchEnd]);

	return { containerRef, pulling, progress };
}
